<template>
    <v-form ref="form" style="width: 100%" v-model="valid">
        <div class="d-flex align-center mb-3">
            <div class="mr-3 flex-grow-1 flex-sm-grow-0">
                <DateInput 
                    v-model="fields.date" 
                    :max="$luxon.now().plus({days: 1}).toISODate()" 
                    @change="changed('date', $event)" 
                    label="Fechas"
                    :disabled="loading"
                    />
            </div>
        </div>
        <template v-if="context == 'comercio'">
            <div class="d-flex align-center flex-wrap mb-3">
                <div class="mr-3 flex-grow-1 flex-sm-grow-0">
                    <SelectorMaster
                        v-model="fields.master_id"
                        :disabled="loading"
                        @change="changed('master_id', $event)"
                        />
                </div>
                <div class="mr-3 flex-grow-1 flex-sm-grow-0">
                    <SelectorComercializadora
                        v-model="fields.comercializadora_id"
                        :disabled="loading  || !this.fields.master_id"
                        :params="{ master_id: fields.master_id }"
                        needs-parent-to-load
                        @change="changed('comercializadora_id', $event)"
                        />
                </div>
                <div class="mr-3 flex-grow-1 flex-sm-grow-0">
                    <SelectorGrupo
                        v-model="fields.grupo_id"
                        :disabled="loading  || !this.fields.comercializadora_id"
                        :params="{ comercializadora_id: fields.comercializadora_id }"
                        needs-parent-to-load
                        @change="changed('grupo_id', $event)"
                        />
                </div>
                <div class="mr-3 flex-grow-1 flex-sm-grow-0">
                    <SelectorTaquilla
                        v-model="fields.taquilla_id"
                        :disabled="loading  || !this.fields.grupo_id"
                        :params="{ grupo_id: fields.grupo_id }"
                        needs-parent-to-load
                        @change="changed('taquilla_id', $event)"
                        />
                </div>
            </div>
        </template>
        <div class="d-flex align-center flex-wrap mb-3">
            <template v-if="context == 'sistema'">
                <div class="mr-3 flex-grow-1 flex-sm-grow-0">
                    <SelectorSistema 
                        v-model="fields.sistema_id"
                        :params="{ sistema_type_slug: sistema_type }"
                        :disabled="loading"
                        @change="changed('sistema_id', $event)"
                        />
                </div>
            </template>
            <div class="mr-3 mb-1 flex-grow-1 flex-sm-grow-0">
                <SelectorJuego
                    :disabled="loading"
                    label="Juego"
                    @change="changed('juego_id', $event)"
                    v-model="fields.juego_id"
                    :juego.sync="juegoSelected"
                    />
            </div>
            <div class="mr-3 mb-1 flex-grow-1 flex-sm-grow-0">
                <SelectorJuegoSorteo
                    :disabled="loading"
                    label="Sorteo"
                    :params="{juego_id: fields.juego_id}"
                    @change="changed('juego_sorteo_id', $event)"
                    v-model="fields.juego_sorteo_id"
                    />
            </div>
            <div class="mr-3 flex-grow-1 flex-sm-grow-0">
                <SelectorNumero v-model="fields.numero" :disabled="!this.fields.juego_id || loading" :params="{juego_id: this.fields.juego_id}" filled/>
            </div>
        </div>
        <v-divider class="my-4" />
        <div class="d-flex align-center flex-wrap">
            <v-card tile outlined>
                <div class="ml-1 mb-1 mr-2">
                    <v-checkbox 
                        v-model="fields.consolidado" 
                        label="Consolidar Por Divisa"
                        hide-details
                        class="ma-0"
                        dense
                        :disabled="loading"
                        ></v-checkbox>
                </div>
                <v-divider class="mb-2" v-if="fields.consolidado"/>
                <div class="ml-2 mb-1" v-if="fields.consolidado">
                    <SelectorDivisaChip v-model="fields.divisaList" :disabled="loading"/>
                </div>
            </v-card>
            <v-spacer/>
            <div v-if="context == 'comercio' && fields.numero">
                <v-select
                    label="Agrupar por comercio"
                    :items="groupByOptions"
                    @change="changed('comercio_to_group', $event)"
                    v-model="fields.comercio_to_group"
                    item-text="name"
                    item-value="id"
                    hide-details
                    dense
                    filled
                    :disabled="loading"
                    />
            </div>
            <div class="d-flex">
                <v-btn text class="mr-3" :disabled="loading" @click="reset()">Reiniciar Filtros</v-btn>
                <v-btn color="primary" :disabled="loading" :loading="loading" @click="search()"><v-icon>mdi-magnify</v-icon>Buscar</v-btn>
            </div>
        </div>
    </v-form>
    
</template>

<script>
// TODO: PASAR LOS SELECTORES DE JUEGO Y SORTEO A SelectorJuego y SelectorJuegoSorteo; crear rutas para estos ya que requieren validaciones segun usuarios.

import Search from '@mixins/search';

export default {
    components: {
    },
    props: {
        context: { default: 'comercio' },
        value: {},
        loading: {
            type: Boolean,
            default: false,
        },
        juego: {},
        sistema_type: { default: 'sistema' }
    },
    mixins: [Search],
    data: function ()  {
        return {
            firstLoaded: false,
            juegoSelected: null,
            defaultFields: {
                date: this.$luxon.local().toISODate(),

                master_id: null,
                comercializadora_id: null,
                grupo_id: null,
                taquilla_id: null,
                sistema_id: null,

                juego_id: null,
                juego_sorteo_id: null,

                numero: null,

                consolidado: false,
                divisaList: [],

                comercio_to_group: null,
            },
        }
    },
    computed: {
        groupByOptions: function () {
            const list = [
                {
                    id: 'comercializadora',
                    name: 'Comercializadora'
                },
                {
                    id: 'grupo',
                    name: 'Grupo'
                },
                {
                    id: 'taquilla',
                    name: 'Taquilla'
                },
            ];

            const filter = ['taquilla', 'grupo', 'comercializadora'].find(filter => this.fields[`${filter}_id`]);

            if (filter) {
                for (const comercio of ['comercializadora', 'grupo', 'taquilla']) {
                    const index = list.findIndex((item => item.id == comercio));

                    if (index == -1) throw new Error('Comercio No Configurado');
                    
                    list.splice(index, 1);

                    if (filter == comercio) break;
                }
            }
            
            return [
                {
                    id: null,
                    name: 'Comercio Filtrado'
                },
                ... list,
            ];
        }
    },
    created: function () {
    },
    mounted: function () {
        this.preloadLists();
    },
    methods: {
        beforeSearch: function () {
            this.firstLoaded = true;
            this.$emit('update:juego', this.juegoSelected);
        },
        preloadLists: function () {
        },
        onChange: function (field) {
            switch(field) {
                case 'master_id':
                    this.fields.comercializadora_id = null;   
                break;
                case 'comercializadora_id':
                    this.fields.grupo_id = null;   
                break;
                case 'grupo_id':
                    this.fields.taquilla_id = null;   
                break;
            }
        },
        checkAndSearch: function () {
            if (this.firstLoaded) return;
            if (!this.fields.juego_sorteo_id) return;
            if (!this.juegoSelected) return;
            this.$emit('update:juego', this.juegoSelected);
            this.search();
        }
    },
    watch: {
        'fields.juego_id': function () {
            this.checkAndSearch();
            this.fields.numero = null;
        },
        'fields.master_id': function () {
            this.comercio_to_group = null;
        },
        'fields.comercializadora_id': function () {
            this.comercio_to_group = null;
        },
        'fields.grupo_id': function () {
            this.comercio_to_group = null;
        },
        'fields.taquilla_id': function () {
            this.comercio_to_group = null;
        },
        'fields.numero': function (numero, old) {
            if (numero === null) this.comercio_to_group = null;
            if (old === null && numero) this.comercio_to_group = null;
        },
        'fields.juego_sorteo_id': function () {
            this.checkAndSearch();
        },
        'juegoSelected': function () {
            this.checkAndSearch();
        }
    }
}
</script>

<style>

</style>